import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { http } from "../../../axios/axios";
import { Helmet } from "react-helmet-async";

const INQUIRY_DETAILS = [
  { value: "product_information", label: "Product Information" },
  { value: "request_quote", label: "Request a Quote" },
  { value: "new_installation", label: "New Installation" },
  { value: "cctv_upgrade", label: "CCTV Upgrade" },
  { value: "site_visit_request", label: "Site Visit Request" },
  { value: "system_expansion", label: "Expand Existing CCTV System" },
  { value: "amc", label: "Annual Maintenance Contract (AMC)" },
  { value: "bulk_purchase", label: "Bulk Purchase" },
  { value: "dealer_distributor_inquiry", label: "Dealer/Distributor Inquiry" },
  { value: "product_demo", label: "Product Demonstration" },
  { value: "consultation", label: "Security Consultation" },
  { value: "price_availability", label: "Price & Availability" },
  { value: "warranty_information", label: "Warranty Information" },
  { value: "other", label: "Other" },
];

const PROPERTY_TYPE = [
  { value: "home", label: "Home" },
  { value: "apartment", label: "Apartment" },
  { value: "office", label: "Office" },
  { value: "shop", label: "Shop" },
  { value: "warehouse", label: "Warehouse" },
  { value: "factory", label: "Factory" },
  { value: "school", label: "School" },
  { value: "college", label: "College" },
  { value: "hospital", label: "Hospital" },
  { value: "hotel", label: "Hotel" },
  { value: "restaurant", label: "Restaurant" },
  { value: "bank", label: "Bank" },
  { value: "showroom", label: "Showroom" },
  { value: "society", label: "Residential Society" },
  { value: "industrial_site", label: "Industrial Site" },
  { value: "construction_site", label: "Construction Site" },
  { value: "government_office", label: "Government Office" },
  { value: "other", label: "Other" },
];

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  register,
  errors = {},
  validation = {},
  as = "input",
  rows = 4,
  options = [],
}) => {
  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm md:px-4 md:py-3 md:text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-semibold text-slate-700 md:mb-2 md:text-base"
      >
        {label}
        {validation?.required && <span className="text-red-500"> *</span>}
      </label>

      {as === "textarea" ? (
        <textarea
          id={name}
          rows={rows}
          placeholder={placeholder}
          {...register(name, validation)}
          className={`${inputClass} resize-none`}
        />
      ) : as === "select" ? (
        <select
          id={name}
          {...register(name, validation)}
          className={inputClass}
        >
          <option value="">{placeholder}</option>

          {options.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          {...register(name, validation)}
          className={inputClass}
        />
      )}

      {errors[name] && (
        <p className="mt-1 text-xs text-red-500 md:text-sm">
          {errors[name].message}
        </p>
      )}
    </div>
  );
};

export default function ProductInquiry() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const fetchProductCategory = async () => {
    try {
      const res = await http.get("/category?limit=1000");

      setCategories(
        res.data.category.map((item) => ({
          value: item._id,
          label: item.name,
        })),
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchProductCategory();
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const res = await http.post("queries", data);

      toast.success(res.data.message);
      reset();

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetchSeo();
  }, []);

  const fetchSeo = async () => {
    try {
      const { data } = await http.get("/seo/Inquiry");
      setSeo(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const seoData = seo || {
    metaTitle: "",
    metaDescription: "",
    metaTags: [],
    schemaScript: null,
  };

  const schemaScript = seoData?.schemaScript
    ? typeof seoData.schemaScript === "string"
      ? seoData.schemaScript
      : JSON.stringify(seoData.schemaScript)
    : null;

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>
        <meta name="description" content={seoData?.metaDescription} />
        <meta
          name="keywords"
          content={
            Array.isArray(seoData?.metaTags)
              ? seoData?.metaTags.join(", ")
              : seoData?.metaTags
          }
        />
        {schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schemaScript }}
          />
        )}
      </Helmet>

      <div className="min-h-screen bg-white pb-20 font-sans">
        <header className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 py-20 px-6 text-center relative overflow-hidden ">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight font-serif">
              Product <span className="text-blue-400">Inquiry</span>
            </h1>

            <p className="hidden md:block mt-6 text-lg text-gray-300 leading-relaxed">
              Looking for the right security solution? Share your requirements
              for CCTV cameras, access control, or other security systems, and
              our team will recommend the best products, provide a quotation, or
              schedule a consultation.
            </p>
          </div>
        </header>
        <section className="px-6 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="rounded-md border border-gray-200 bg-white px-4 py-8 lg:p-8">
              <h2 className="text-2xl font-bold text-slate-900 font-serif">
                Fill Out the <span className="text-blue-400">Inquiry Form</span>
              </h2>
              <p className="mt-2 text-gray-600">
                Complete the form below, and our team will contact you with
                product recommendations, pricing, or a customized security
                solution.
              </p>

              <form
                className="mt-8 space-y-6"
                onSubmit={handleSubmit(onSubmit)}
                autoComplete="on"
              >
                <InputField
                  label="Full Name"
                  name={"name"}
                  placeholder={"Enter your full name"}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Full name is required",
                  }}
                />

                <InputField
                  label={"Phone Number"}
                  name={"phone"}
                  type="tel"
                  placeholder={"Enter 10-digit phone number"}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Phone number is required",
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message: "Enter a valid phone number",
                    },
                  }}
                />

                <InputField
                  label={"Email"}
                  name={"email"}
                  type={"email"}
                  placeholder="john@gmail.com"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Email ID is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email ID",
                    },
                  }}
                />

                <InputField
                  label={"Company Name"}
                  name={"companyName"}
                  placeholder={"Enter your company name (optional)"}
                  register={register}
                />

                <InputField
                  label={"City"}
                  name={"city"}
                  placeholder={"Enter your city"}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "City is required",
                  }}
                />

                <InputField
                  label={"State"}
                  name={"state"}
                  placeholder={"Enter your state"}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "State is required",
                  }}
                />

                <InputField
                  label="Inquiry Details"
                  name="inquiryDetails"
                  placeholder="Select an inquiry type"
                  as="select"
                  options={INQUIRY_DETAILS}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select an inquiry detail",
                  }}
                />

                <InputField
                  label={"Property Type"}
                  name={"propertyType"}
                  as="select"
                  placeholder={"Select your property type"}
                  options={PROPERTY_TYPE}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select your property type",
                  }}
                />

                <InputField
                  label="Estimated Quantity Required"
                  name="estimatedQuantity"
                  placeholder="e.g., 8 CCTV cameras or 2 DVRs"
                  register={register}
                />

                <InputField
                  label="Preferred Brand"
                  name="preferredBrand"
                  placeholder="e.g., Hikvision, Dahua, CP Plus, or Any"
                  register={register}
                />

                <InputField
                  label="Product Category"
                  name="productCategory"
                  as="select"
                  options={categories}
                  placeholder="Select a product category"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select a product category",
                  }}
                />

                <InputField
                  label="Budget Range"
                  name="budgetRange"
                  placeholder="e.g., ₹50,000 - ₹10,00,000"
                  register={register}
                />

                <InputField
                  label="Preferred Contact Method"
                  name="preferredContactMethod"
                  as="select"
                  placeholder="Select your preferred contact method"
                  options={[
                    { value: "phone", label: "Phone Call" },
                    { value: "whatsapp", label: "WhatsApp" },
                    { value: "email", label: "Email" },
                  ]}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select your preferred contact method",
                  }}
                />

                <InputField
                  label="Message / Requirements"
                  name="message"
                  as="textarea"
                  rows={5}
                  placeholder="Describe your requirements or ask your question."
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please describe your requirements",
                  }}
                />

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full rounded-md bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? "Submitting..." : "Submit Inquiry"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
